import { defineComponent, PropType } from 'vue'
import { BModal } from 'bootstrap-vue'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { Item, SearchResult, SpecimenResource } from '@skinsoft/types'
import { itemSelectors, specimenSelectors } from '@skinsoft/selectors'
import { searchResultSelectors } from '@skinsoft/search-data-store'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { LINK_MOVEMENT_PAGE_NAVIGATE, SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED } from '../../../events'
import { SelectItemMultipleForm } from './select-item-multiple-form'
import { BarCodeEditor } from '../../../app/component/action-bar/map-icon-toggle/bar-code-editor'

require('./select-item-multiple-modal.scss')

export interface SIMMItemSelected {
    id: string,
    type: string,
    item?: SearchResult | Item,
    specimen?: SpecimenResource
}

export const SelectItemMultipleModal = defineComponent({
    mixins: [reactiveVueMixin],
    emits: ['item-selected', SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED],
    components: {
        sSelectItemMultipleForm: SelectItemMultipleForm,
        sBarCodeEditor: BarCodeEditor,
    },
    props: {
        canChainAdd: {
            type: Boolean,
            default: true
        },
        isUpdating: Boolean,
        canChangeMode: {
            type: Boolean,
            default: false,
        },
        showLinksToMovements: {
            type: Boolean,
            default: false
        },
        excludedIds: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        excludedItemsWarningMessageKey: {
            type: String,
        },
        types: Array as PropType<string[]>,
        emitChainAddResultsTogether: {
            type: Boolean,
            default: false,
        },
        maxNbChainAdd: {
            type: Number,
            default: 50,
        }
    },
    template: require('./select-item-multiple-modal.html'),
    data() {
        return {
            mounted: false,
            spinnerIcon: faCircleNotch,
            locationSelector: true,
            shouldChainAdd: false,
            selectedResults: [] as (Item | SearchResult | SpecimenResource)[], // for emitting all results at once when chain addding
            isLocallyUpdating: false,
            LINK_MOVEMENT_PAGE_NAVIGATE: LINK_MOVEMENT_PAGE_NAVIGATE,
        }
    },
    methods: {
        open() {
            console.log('TEST TYPES');
            console.log(this.types);
            (this.$refs.modal as InstanceType<typeof BModal>).show()
        },
        close() {
            (this.$refs.modal as InstanceType<typeof BModal>).hide()
        },
        onShow() {
            this.mounted = true
        },
        onHide() {
            this.emitMultipleItemsSelected()
            this.mounted = false
        },
        emitMultipleItemsSelected() {
            if(this.emitChainAddResultsTogether && this.selectedResults.length) {
                const items: SIMMItemSelected[] = this.selectedResults.map(item => {
                    switch (item.entityType?.toLowerCase()) {
                        case 'specimen':
                            return {
                                id: specimenSelectors.id(item as SpecimenResource),
                                type: 'specimen',
                                specimen: item,
                            } as SIMMItemSelected
                        default:
                            return {
                                id: (item as any).itemId ??
                                    searchResultSelectors.id(item as SearchResult) ??
                                    itemSelectors.id(item as Item),
                                type: (item as any).itemType ??
                                    searchResultSelectors.itemType(item as SearchResult) ??
                                    itemSelectors.type(item as Item),
                                item: item,
                            } as SIMMItemSelected
                }})

                this.$emit(
                    SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED,
                    items
                )

                this.selectedResults = []
            }
        },
        onItemSelected(item: SearchResult | Item | SpecimenResource, searchContent: string, currentPage: number) {
            if(this.canChainAdd && this.shouldChainAdd) {
                if(this.emitChainAddResultsTogether) {
                    this.isLocallyUpdating = true
                    this.selectedResults.push(item)
                    // timeout to force refresh of children
                    // otherwise the change false > true > false is to quick to be detected
                    setTimeout(() => {
                        this.isLocallyUpdating = false
                    }, 200)

                    if(this.selectedResults.length > this.maxNbChainAdd) {
                        // calling close will emit the event with all the selected results
                        // because this.onHide calls this.emitMultipleItemsSelected
                        this.close()
                    }
                    return
                }
            }

            switch (item.entityType?.toLowerCase()) {
                case 'specimen':
                    this.$emit(
                        'item-selected',
                        {
                            id: specimenSelectors.id(item as SpecimenResource),
                            type: 'specimen',
                            specimen: item,
                        } as SIMMItemSelected
                    )
                    break
                default:
                    this.$emit(
                        'item-selected',
                        {
                            id: (item as any).itemId ??
                                searchResultSelectors.id(item as SearchResult) ??
                                itemSelectors.id(item as Item),
                            type: (item as any).itemType ??
                                searchResultSelectors.itemType(item as SearchResult) ??
                                itemSelectors.type(item as Item),
                            item: item,
                        } as SIMMItemSelected
                    )
            }
            if (!this.canChainAdd || !this.shouldChainAdd) {
                this.close()
            }
        },
        resetSearch() {
            this.$nextTick(() => (this.$refs.itemSelect as InstanceType<typeof SelectItemMultipleForm>)?.resetSearch())
        },
    },
    computed: {
        computedExcludedIds(): string[] {
            return this.excludedIds.concat(
                this.selectedResults.map(item => {
                    return item.id
                })
            )
        },
    },
    watch: {
        shouldChainAdd() {
            this.emitMultipleItemsSelected()
        },
    },
})
